import React from 'react'
import { apiCall, secondaryApi } from './ApiUtils'
import { loadable } from '../components/Layout/Common/Loadable';

import websitePerformanceImg from "../../assets/images/alert-icons/website-performance.png"
import pageSpeedImg from "../../assets/images/alert-icons/page-speed.png"
import searchIconImg from "../../assets/images/alert-icons/search-icon.png"
import socialMediaImg from "../../assets/images/alert-icons/social-media-icon.png"
import conversionRateImg from "../../assets/images/alert-icons/conversion-rate.png"
import adSpendImg from "../../assets/images/alert-icons/ad-spend.png"
import goalCompletionsImg from "../../assets/images/alert-icons/goal-completions.png"
import brandReferralsImg from "../../assets/images/alert-icons/brand-referrals.png"
import ecomCartImg from "../../assets/images/alert-icons/ecom-cart.png"
import gmbImg from "../../assets/images/big-gmb.png"
import emailImg from "../../assets/images/mailto-icon.png"
import crmImg from "../../assets/images/alert-icons/booking-crm.png"

const Image = loadable(() => import('../components/Layout/Common/Image'), {
    cacheKey: props => props.src,
})

export const LATEST_AUDIT_VERSION = 3
export const DEFAULT_AUDIT_VARIANCE = 0.05

export const getAuditsBySite = (token, siteId, lastEvalKey = null, yearMonth=null) => apiCall(`sites/${siteId}/audits?${lastEvalKey ? "lastEvalKey="+ JSON.stringify(lastEvalKey) : ""}${yearMonth ? "yearMonth="+ yearMonth.toString() : ""}`, {
    method: 'GET'
}, token, secondaryApi)

export const getAudit = (token, auditId) => apiCall(`audits/${auditId}`,{
    method: 'GET'
}, token, secondaryApi)

export const updateAudit = (token, auditId, body) => apiCall(`audits/${auditId}`,{
    method: 'PUT',
    body
}, token, secondaryApi)

export const sendEmail = (token, auditId, body) => apiCall(`audits/${auditId}/email`,{
    method: 'POST',
    body
}, token)

export const generateAudit = (siteId, body) => apiCall(`admin/audits/${siteId}`, {
    method: 'POST',
    body
}, null, secondaryApi)

export const auditCaseStudyMetrics = {
    adSpend: 1365,
    afterHours: 0.59,
    avgPageLoadTime: 7,
    mobileTti: 7.3,
    bounceRate: 0.63,
    costPerLead: 85,
    desktopPerformance: 0.812,
    gcr: 0.03,
    goals: 50,
    mobilePerformance: 0.543,
    otherCampaignGcr: 0,
    otherCampaignGoals: 0,
    otherCampaignSessions: 0,
    paidAdsGoals: 34,
    paidAdsGcr: 0,
    sessions: 1358,
    seoGcr: 0.04,
    socialGcr: 0.02,
    totalFans: 0,
    newFans: 0,
    fanGrowth: 0,
    orgGrowthYOY: 0,
    orgGrowthLastQtr: 0,
    referralLeadVsOverall: 0.20,
    speedToBookingMinutes: 5,
    speedToBookingOtherMinutes: 4320
}

export const smbAuditCaseStudyMetrics = {
    avgPageLoadTime: 7,
    bounceRate: 0.63,
    orgGrowthYOY: 0,
    mobilePerformance: 0.543,
    desktopPerformance: 0.812,
    fanGrowth: 0,
    goals: 0,
    gmbViews: 0,
    emailGoals: 0
}

export const ecommerceAuditCaseStudyMetrics = {
    ...smbAuditCaseStudyMetrics,
    ROAS: 1.5,
    transactions: 0
}

export const franchiseCaseStudy = {
    ...auditCaseStudyMetrics,
    paidLeads: 34,
    goalsAfterHours: 0,
    mobileGcr: 0.04,
    mobileSessions: 0.64,
    returnedSessions: 0.15,
    costPerClick: 2,
    leadsPercentOverall: 0.28,
    brandRefSessionsPercentOverall: 0.28,
    pageLoad: 7,
    revenue: null,
    unitSales: null,
    totalFans: null,
    newFans: null,
    totalFinanciallyQualifiedLeads: null,
    totalFinanciallyQualifiedPaidLeads: null
}

export const standardAuditCaseStudyMetrics = {
    bounceRate: 0.63,
    avgPageLoadTime: 7,
    mobileTti: 7.3,
    mobilePerformance: 0.543,
    desktopPerformance: 0.812,
    goals: 0,
    seoGoals: 0,
    emailGoals: 0,
    socialGoals: 0,
    fanGrowth: 0,
    gmbActions: 0,
    referralLeadVsOverall: 0.15,
    referralSessionVsOverall: 0.15
}

export const ecomAuditCaseStudyMetrics = {
    ...standardAuditCaseStudyMetrics,
    ROAS: 1.5
}

export const franDevCaseStudyV3 = {
    ...standardAuditCaseStudyMetrics
}

export const auditVariance = {
    fanGrowth: 0,
    ROAS: 0.10,
    bounceRate: DEFAULT_AUDIT_VARIANCE,
    referralLeadVsOverall: 0.15,
    referralSessionVsOverall: 0.15,
    gmbActions: 0.20,
    emailGoals: DEFAULT_AUDIT_VARIANCE,
    mobilePerformance: DEFAULT_AUDIT_VARIANCE,
    desktopPerformance: DEFAULT_AUDIT_VARIANCE,
    goals: DEFAULT_AUDIT_VARIANCE,
    seoGoals: DEFAULT_AUDIT_VARIANCE,
    socialGoals: DEFAULT_AUDIT_VARIANCE,
    avgPageLoadTime: DEFAULT_AUDIT_VARIANCE,
    newFans: 0,
    mobileTti: DEFAULT_AUDIT_VARIANCE
}

export const auditMetricsDictionary = {
    sessions: { title: "Sessions", titleAbbrev: "SESS"},
    goals: { title: "Goals", titleAbbrev: "GLS"},
    gcr: { title: "Goal Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(1), suffix: "%"},
    bounceRate: { title: "Bounce Rate", titleAbbrev: "BNC RATE", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    avgSessDuration: { title: "Average Session Duration"},
    avgPageLoadTime: { title: "Page Load", titleAbbrev: "PG LOAD", suffix: "(sec)"},
    mobileTti: { title: "Mobile Time to Interactive", titleAbbrev: "MOB TTI", modifier: value => (value).toFixed(1), suffix: "(sec)"},
    mobilePerformance: { title: "Mobile Performance", titleAbbrev: "MOB PERF", modifier: value => (parseFloat(value) * 100).toFixed(0), suffix: "%"},
    desktopPerformance: { title: "Desktop Performance", titleAbbrev: "DKTP PERF", modifier: value => (parseFloat(value) * 100).toFixed(0), suffix: "%"},
    seoSessions: { title: "Sessions", titleAbbrev: "SESS"},
    seoGoals: { title: "Goals", titleAbbrev: "GLS"},
    seoGcr: { title: "Goal Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    orgGrowthYOY: { title: "Organic Growth YOY", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    orgGrowthLastQtr: { title: "Growth Last Qtr YOY", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    socialGcr: { title: "Goal Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    socialSessions: { title: "Sessions", titleAbbrev: "SESS"},
    socialGoals: { title: "Goals"}, titleAbbrev: "GLS",
    paidSessions: { title: "Sessions", titleAbbrev: "SESS"},
    paidLeads: { title: "Leads", titleAbbrev: "LDS"},
    paidAdsGoals: { title: "Leads", titleAbbrev: "LDS"},
    paidAdsGcr: { title: "Lead Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    costPerLead: { title: "Cost Per Lead", titleAbbrev: "CPL", prefix: "$"},
    costPerClick: { title: "Cost Per Click", titleAbbrev: "CPC", prefix: "$", modifier: value => value.toFixed(2)},
    afterHours: { title: "After Hours", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    adSpend:  { title: "Ad Spend", titleAbbrev: "SPEND", modifier: value => value.toFixed(0), prefix: "$"},
    newFans:  { title: "Fan Growth", titleAbbrev: "FAN<br />GROWTH"},
    otherCampaignGcr:  { title: "GCR", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    otherCampaignGoals:  { title: "Conversions"},
    otherCampaignSessions:  { title: "Sessions"},
    totalFans:  { title: "Fans", titleAbbrev: "FANS"},
    fanGrowth:  { title: "Fan Growth", titleAbbrev: "FAN<br />GROWTH", modifier: value => (value * 100).toFixed(1), suffix: "%"},
    gmbViews:  { title: "Views/Impressions", titleAbbrev: "VIEWS"},
    gmbActions:  { title: "Actions", titleAbbrev: "ACT"},
    gmbGcr:  { title: "GBP Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(1), suffix: "%"},
    emailSessions:  { title: "Sessions", titleAbbrev: "SESS"},
    emailGoals:  { title: "Goals", titleAbbrev: "GLS"},
    emailGcr:  { title: "Goal Conversion Rate", titleAbbrev: "GCR", modifier: value => (value * 100).toFixed(1), suffix: "%"},
    fbCheckins: { title: "Checkins"},
    referralLeadVsOverall:  { title: "Referral Leads %", titleAbbrev: "LEADS %", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    referralSessionVsOverall:  { title: "Referral Sessions %", titleAbbrev: "SESSIONS %", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    ROAS: { title: "ROAS", titleAbbrev: "ROAS", modifier: value => value.toFixed(2)},
    averageOrderValue: { title: "AOV", titleAbbrev: "AOV", modifier: value => value.toFixed(0), prefix: "$"},
    transactions: { title: "Transactions", titleAbbrev: "TRN"},
    revenue: { title: "Revenue", titleAbbrev: "REV", modifier: value => value.toFixed(0), prefix: "$"},
    bookingRate: { title: "Booking Rate", titleAbbrev: "BOOK<br />RATE %", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    keptRate: { title: "Kept Rate", titleAbbrev: "KEPT<br />RATE %", modifier: value => (value * 100).toFixed(0), suffix: "%"},
    speedToBookingMinutes: { title: "Speed to Booking", titleAbbrev: "SPEED<br />TO BOOK", modifier: value => value ? (value).toFixed(0) : value},
}

export const auditMetricsForCompare = {
    sessions: { opposite: false, excludeFromScore: ["smbstats","veteranstats","ecommercestats","standard", "frandevNew", "ecommerce"]  },
    goals: { opposite: false, caseStudyOverride: "+ Growth" },
    gcr: { opposite: false, excludeFromScore: ["smbstats","veteranstats","ecommercestats","standard", "frandevNew", "ecommerce"] },
    bounceRate: { opposite: true },
    avgSessDuration: { opposite: false, typesDisabled: ["frandev", "", undefined], disableIfUndefined: true, excludeFromScore: true, comparisonValue: value => {
        if(value){
            let time = value.split(":") 
            let total = 0
            
            if(time[0]){
                total += parseInt(time[0]) * 60
            }
            if(time[1]){
                total += parseInt(time[1])
            }

            return total
        }

        return value
    }},
    avgPageLoadTime: { opposite: true, disableIfUndefined: true },
    mobileTti: { opposite: true, disableIfUndefined: true },
    mobilePerformance: { opposite: false },
    desktopPerformance: { opposite: false },
    seoSessions: { opposite: false, typesDisabled: ["smbstats","veteranstats","ecommercestats"], excludeFromScore: ["standard", "frandevNew", "ecommerce"]  },
    seoGoals: { opposite: false, caseStudyOverride: "+ Growth", typesDisabled: ["smbstats","veteranstats","ecommercestats"]  },
    seoGcr: { opposite: false, typesDisabled: ["smbstats","veteranstats","ecommercestats"], excludeFromScore: ["standard", "frandevNew", "ecommerce"]  },
    orgGrowthYOY: { opposite: false, caseStudyOverride: "+ Growth", disableIfUndefined: true, excludeFromScore: ["frandevNew"] },
    orgGrowthLastQtr: { opposite: false, caseStudyOverride: "+ Growth", disableIfUndefined: true, excludeFromScore: ["smbstats","veteranstats", "ecommercestats", "frandevNew"] },
    socialSessions: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true },
    socialGoals: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: ["smbstats","veteranstats","ecommercestats"], caseStudyOverride: "+ Growth" },
    socialGcr: { opposite: false, typesDisabled: ["smbstats","veteranstats","ecommercestats"], excludeFromScore: true },
    fbCheckins: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true },
    totalFans: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true },
    fanGrowth:  { opposite: false, typesDisabled: ["frandev", "", undefined], caseStudyOverride: "+ Growth", scoreToBaselineOverride: true },
    gmbViews:  { opposite: false, disableIfUndefined: true, typesDisabled: ["frandev", "", undefined], excludeFromScore: ["standard", "frandevNew", "ecommerce"] },
    gmbActions:  { opposite: false, disableIfUndefined: true, caseStudyOverride: "+ Growth", typesDisabled: ["frandev", "", undefined], excludeFromScore: ["smbstats","veteranstats", "ecommercestats", "frandevNew", "ecommerce"] },
    gmbGcr:  { opposite: false, disableIfUndefined: true, typesDisabled: ["frandev", "", undefined], excludeFromScore: true, disableIfUndefined: true },
    emailSessions: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true, disableIfUndefined: true},
    emailGoals: { opposite: false, typesDisabled: ["frandev", "", undefined], disableIfUndefined: true, caseStudyOverride: "+ Growth"},
    emailGcr: { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true, disableIfUndefined: true},
    adSpend:  { opposite: false, typesDisabled: ["frandev", "", undefined], excludeFromScore: true },
    paidSessions: { opposite: false, typesDisabled: ["frandev","smbstats","veteranstats","ecommercestats"], excludeFromScore: true  },
    paidLeads: { opposite: false, typesDisabled: ["frandev","smbstats","veteranstats","ecommercestats"], excludeFromScore: ["standard", "frandevNew", "ecommerce"]  },
    paidAdsGoals: { opposite: false, excludeFromScore: ["smbstats","veteranstats","ecommercestats","standard", "frandevNew", "ecommerce"]  },
    paidAdsGcr: { opposite: false, typesDisabled: ["smbstats","veteranstats","ecommercestats"], excludeFromScore: ["standard", "frandevNew", "ecommerce"] },
    costPerLead: { opposite: true, excludeFromScore: ["standard", "frandevNew", "ecommerce"], disableIfUndefined: true },
    costPerClick: { opposite: true, typesDisabled: ["frandev", "", undefined], excludeFromScore: true },
    afterHours: { opposite: false, excludeFromScore: true },
    newFans: {opposite: false, caseStudyOverride: "+ Growth", excludeFromScore: ["smbstats","veteranstats","ecommercestats", "frandevNew", "ecommerce", "standard"]},
    referralLeadVsOverall: { opposite: false, disableIfUndefined: true },
    referralSessionVsOverall: { opposite: false, disableIfUndefined: true },
    ROAS: { opposite: false, typesDisabled: ["frandev", "", undefined, "smbstats","veteranstats", "standard", "frandevNew"] },
    averageOrderValue: {opposite: false, typesDisabled: ["frandev", "", undefined, "smbstats","veteranstats", "standard", "frandevNew"], excludeFromScore: true},
    transactions: {opposite: false, caseStudyOverride: "+ Growth", typesDisabled: ["frandev", "", undefined, "smbstats", "veteranstats", "standard", "frandevNew"], excludeFromScore: ["ecommerce"]},
    revenue:{opposite: false, disableIfUndefined: true, typesDisabled: ["frandev", "", undefined, "revenue", "smbstats","veteranstats", "standard", "frandevNew"], excludeFromScore: true},
    bookingRate: { opposite: false, typesDisabled: ["frandev", "", undefined, "smbstats","veteranstats", "standard", "ecommerce"], excludeFromScore: true},
    keptRate: { opposite: false, typesDisabled: ["frandev", "", undefined, "smbstats","veteranstats", "standard", "ecommerce"], excludeFromScore: true},
    speedToBookingMinutes: { opposite: false, typesDisabled: ["frandev", "", undefined, "smbstats","veteranstats", "standard", "ecommerce"], excludeFromScore: true}
}

export const auditMetricGroups = [
    { title: "Website Performance", metrics: ["sessions", "bounceRate", "avgPageLoadTime"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={websitePerformanceImg} />},
    { title: "Google Page Speed Insights", metrics: ["mobilePerformance", "desktopPerformance"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={pageSpeedImg} />},
    { title: "Search Engine Performance", metrics: ["seoGcr", "orgGrowthYOY", "orgGrowthLastQtr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={searchIconImg} />},
    { title: "Social Performance", metrics: ["socialGcr", "newFans"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={socialMediaImg} />},
    { title: "Other Campaign Performance", metrics: ["otherCampaignSessions", "otherCampaignGoals", "otherCampaignGcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={conversionRateImg} />},
    { title: "Paid Campaign Summary", metrics: ["adSpend", "costPerLead", "paidAdsGoals", "paidAdsGcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={adSpendImg} />},,
    { title: "Goals & Conversions", metrics: ["goals", "gcr", "afterHours"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={goalCompletionsImg} />},
    { title: "Brand Referrals", metrics: ["referralLeadVsOverall"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={brandReferralsImg} />},
]

export const smbAuditMetricGroups = [
    { title: "Website Performance", metrics: ["sessions", "bounceRate", "avgSessDuration", "avgPageLoadTime"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={websitePerformanceImg} />},
    { title: "Google Page Speed Insights", metrics: ["mobilePerformance", "desktopPerformance"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={pageSpeedImg} />},
    { title: "Search Engine Performance", metrics: ["seoGcr", "orgGrowthYOY", "orgGrowthLastQtr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={searchIconImg} />},
    { title: "Social Performance", metrics: ["socialSessions", "socialGoals", "fbCheckins", "totalFans", "fanGrowth"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={socialMediaImg} />},
    { title: "Email Marketing Performance", metrics: ["emailSessions", "emailGoals", "emailGcr"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={emailImg} />},
    { title: "Paid Campaign Summary", metrics: ["adSpend", "paidAdsGoals", "costPerClick"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={adSpendImg} />},,
    { title: "Goals & Conversions", metrics: ["goals", "gcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={goalCompletionsImg} />},
    { title: "Google Business Profile", metrics: ["gmbViews", "gmbActions", "gmbGcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={gmbImg} />},
]

export const ecommerceAuditMetricGroups = [
    { title: "Ecommerce", metrics: ["ROAS", "averageOrderValue", "transactions"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={ecomCartImg} />},
    ...smbAuditMetricGroups
]

export const standardAuditMetricGroups = [ 
    { title: "Website Performance", metrics: ["sessions", "bounceRate", "avgPageLoadTime"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={websitePerformanceImg} />},
    { title: "Google Page Speed Insights", metrics: ["mobilePerformance", "desktopPerformance", "mobileTti"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={pageSpeedImg} />},
    { title: "Brand Referrals", metrics: ["referralLeadVsOverall", "referralSessionVsOverall"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={brandReferralsImg} />},
    { title: "Search Engine Performance", metrics: ["seoSessions", "seoGoals", "seoGcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={searchIconImg} />},
    { title: "Social Media Performance", metrics: ["socialSessions", "socialGoals", "socialGcr", "totalFans", "fanGrowth"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={socialMediaImg} />},
    { title: "Email Marketing Performance", metrics: ["emailSessions", "emailGoals", "emailGcr"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={emailImg} />},
    { title: "Paid Campaign Summary", metrics: ["paidSessions", "paidAdsGoals", "paidAdsGcr", "adSpend", "costPerLead"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={adSpendImg} />},,
    { title: "Goals & Conversions", metrics: ["goals", "gcr"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={goalCompletionsImg} />},
    { title: "Google Business Profile", metrics: ["gmbViews", "gmbActions", "gmbGcr"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={gmbImg} />},
]

let franDevMetricGroups = [ 
    ...standardAuditMetricGroups,
    { title: "CRM Booking Stats", metrics: ["speedToBookingMinutes", "bookingRate", "keptRate"], disableIfUndefined: true, image: () => <Image width={64} height={64} className="mx-auto d-block" src={crmImg} />},
]

export { franDevMetricGroups }

export const newEcomAuditMetricGroups = [ 
    { title: "Ecommerce", metrics: ["revenue", "transactions", "averageOrderValue", "ROAS"], image: () => <Image width={64} height={64} className="mx-auto d-block" src={ecomCartImg} />},
    ...standardAuditMetricGroups
]


export const getColorByComparison =(a, b, opposite=false, variance=0, scoreToBaselineOverride=false) => {
    if(scoreToBaselineOverride && a > 0){
        return ["green", 1, 0]
    }
    if(a < b && opposite == false) {
        let passScore = !((a * (1 + variance)) < b) ? 1 : 0
        return [variance > 0 && passScore > 0 ? "black" : "red", passScore, (1 - a / b) * 100]
    }
    if(a > b && opposite == false) {
        let passScore = ((a * (1 + variance)) > b && a < b) ? 1 : 0
        return [variance > 0 && passScore > 0 ? "black" : "green", a > b ? 1 : passScore, (1 - b / a) * 100]
    }
  
    if(a < b && opposite && a > 0) {
        let passScore = (a < (b * (1 + variance)) && a > b) ? 1 : 0
        return [variance > 0 && passScore > 0 ? "black" : "green", a < b ? 1 : passScore, (1 - a / b) * 100]
    }
    if(a > b && opposite) {
        let passScore = !(a > (b * (1 + variance))) ? 1 : 0
        return [variance > 0 && passScore > 0 ? "black" : "red", passScore, (1 - b / a) * 100]
    }

  
    return ["black", variance > 0 && a > 0 ? 1 : 0, 0]
}

export const StatusIndicator = ({version, color, opposite}) => {

    if(version === 2){
        if(color === "green" && !opposite){
            return <span style={{color: "#0cce6b"}}>&#9650;</span>
        } else if(color === "green" && opposite){
            return <span style={{color: "#0cce6b"}}>&#9660;</span>
        }

        if(color === "red" && opposite){
            return <span style={{color: "red"}}>&#9650;</span>
        }

        return <span style={{color: "red"}}>&#9660;</span>
    }

    if(color === "green"){
        return <div className="circle"></div>
    }

    return <div className="triangle"></div>
}

export const StatusIndicatorV3 = ({version, color, opposite}) => {
    if(color === "green" && !opposite){
        return <span style={{fontSize: "8pt", color: "#0cce6b"}}><i class="fas fa-chevron-circle-up text-success"></i></span>
    } else if(color === "green" && opposite){
        return <span style={{fontSize: "8pt", color: "#0cce6b"}}><i class="fas fa-chevron-circle-down text-success"></i></span>
    }

    if(color === "red" && opposite){
        return <span style={{fontSize: "8pt", color: "red"}}><i class="fas fa-chevron-circle-up"></i></span>
    }

    if(color === "black"){
        <span style={{fontSize: "8pt"}}>
            <i class="fas fa-minus-circle"></i>
        </span>
    }

    return <span style={{fontSize: "8pt", color: "red"}}>
        <i class="fas fa-chevron-circle-down"></i>
    </span>
}

export const getScoreStatsForAudit = (audit, metricsGrouping="metrics", isBaseline=true) => {
    let metricCaseStudyCompare = standardAuditCaseStudyMetrics
    
    if(audit.type === "standard"){
        metricCaseStudyCompare = standardAuditCaseStudyMetrics
    } else if(audit.type === "frandevNew" && audit.version === LATEST_AUDIT_VERSION){
        metricCaseStudyCompare = franDevCaseStudyV3
    } else if(audit.type === "ecommerce"){
        metricCaseStudyCompare = ecomAuditCaseStudyMetrics
    }

    const { score, scoreDivide } =  Object.keys(audit.metrics).reduce((prev, curr, idx, arr) => {
        if(auditMetricsForCompare[curr] && 
            (auditMetricsForCompare[curr].typesDisabled === undefined || 
                !auditMetricsForCompare[curr].typesDisabled.includes(audit.type))) {
                        
            if(!auditMetricsForCompare[curr].excludeFromScore ||
                (Array.isArray(auditMetricsForCompare[curr].excludeFromScore) && 
                !auditMetricsForCompare[curr].excludeFromScore.includes(audit.type))) {

                let [c, s, d] = getColorByComparison(
                    audit[metricsGrouping] ? (audit[metricsGrouping][curr] ?? 0) : 0, 
                    isBaseline ? metricCaseStudyCompare[curr] : audit.metrics[curr], 
                    auditMetricsForCompare[curr].opposite,
                    isBaseline ? 0 : auditVariance[curr] ? auditVariance[curr] : 0,
                    auditMetricsForCompare[curr].scoreToBaselineOverride ?? false
                )

                prev.score += s
                prev.scoreDivide += 1
            }
        }
        
        return prev
    }, {
        score: 0,
        scoreDivide: 0
    })

    return parseInt(Math.round(score / scoreDivide * 100))
}